import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Operator-facing button + progress card for the Warm-All Research
 * Presets flow. Sequentially fetches klines for every preset
 * (10 single + 3 composite sub-windows) × the chosen TFs so the
 * subsequent Backtest / WF / Ablation runs hit a warm disk cache.
 *
 * Pure infrastructure UX. ZERO impact on backtest math, strategy
 * logic, live trading. Cancellable, resumable across restarts via
 * the persistent `warm_all_jobs` collection.
 */
export function WarmAllPresetsButton({ strategyProfileId, symbol = "BTCUSDT", testidPrefix = "bt" }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const tid = (k) => `${testidPrefix}-warm-all-${k}`;

  // Poll while a job is active.
  useEffect(() => {
    if (!job?.id) return;
    if (["completed", "completed_with_failures", "failed", "cancelled"].includes(job?.status)) return;
    const tick = async () => {
      try {
        const d = await api.researchCacheWarmAllGet(job.id);
        setJob(d);
        if (["completed", "completed_with_failures", "failed", "cancelled"].includes(d.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
          toast.success(
            `Warm All ${d.status} — fetched ${d.fetched_cells ?? 0} cells` +
            (d.failed_cells ? `, ${d.failed_cells} failed` : ""),
          );
        }
      } catch {}
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [job?.id, job?.status]);

  const onStart = async () => {
    if (!strategyProfileId) {
      toast.error("Pick a strategy profile first so the warmer knows which timeframes to fetch.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.researchCacheWarmAll({
        strategy_profile_id: strategyProfileId,
        symbol,
        include_composite: true,
      });
      setJob({ id: r.id, status: r.status });
      toast.info(`Warm All started · tfs=${(r.timeframes || []).join(",")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async () => {
    if (!job?.id) return;
    try {
      await api.researchCacheWarmAllCancel(job.id);
      toast.info("Cancel requested — runner will stop after current task.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const isRunning = job?.id && ["pending", "running"].includes(job?.status);
  const progressPct = job?.progress_percent ?? 0;

  return (
    <div className="flex flex-col gap-1.5" data-testid={tid("wrapper")}>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={tid("button")}
          disabled={busy || isRunning}
          onClick={onStart}
          className="h-6 px-2 text-[10px] uppercase tracking-widest rounded-sm bg-[#1A1A22] border-border hover:bg-[#262633] disabled:opacity-40"
          title="Warm klines cache for all single + composite preset windows"
        >
          {isRunning ? "Warming all…" : "Warm all presets"}
        </Button>
        {isRunning ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid={tid("cancel")}
            onClick={onCancel}
            className="h-6 px-2 text-[10px] uppercase tracking-widest rounded-sm border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
          >Cancel</Button>
        ) : null}
        {job?.id && !isRunning ? (
          <span data-testid={tid("done")} className="text-[10px] uppercase tracking-widest text-[#71717A]">
            {job.status} · {job.completed_tasks ?? 0}/{job.total_tasks ?? "?"} tasks · {job.fetched_cells ?? 0} cells fetched
          </span>
        ) : null}
      </div>
      {isRunning ? (
        <div className="flex items-center gap-2" data-testid={tid("progress")}>
          <div className="flex-1 h-1.5 bg-[#1A1A22] rounded-sm overflow-hidden">
            <div
              className="h-full bg-[#00E676]"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-[10px] font-mono text-[#71717A]">
            {Math.round(progressPct)}% · {job?.current_preset_label || "…"}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default WarmAllPresetsButton;
